import React, { useMemo } from 'react';
import { Select, Space } from 'antd';
import { trpc } from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';
import { SelfAssignButton } from './SelfAssignButton';

interface SingleUserSelectProps {
  value?: string;
  onChange?: (value: string | undefined) => void;
  allowClear?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  /** 「自分が担当」ボタンを表示するか */
  showAssignMeButton?: boolean;
  /** 「自分が担当」ボタンのツールチップ */
  assignMeTooltip?: string;
}

type UserSummary = { id: string; name?: string | null; email?: string | null };

interface UserListResponse {
  data?: UserSummary[];
  items?: UserSummary[];
  users?: UserSummary[];
  total?: number;
  pagination?: { total?: number };
}

export const SingleUserSelect: React.FC<SingleUserSelectProps> = ({
  value,
  onChange,
  allowClear = true,
  placeholder = '担当者を選択',
  style,
  disabled = false,
  showAssignMeButton = true,
  assignMeTooltip = '自分を担当に設定',
}) => {
  const authUser = useAuthStore((state) => state.user);
  const canManageUsers = useMemo(
    () => ['admin', 'manager'].includes(authUser?.role ?? ''),
    [authUser?.role],
  );

  // ユーザー一覧取得
  const { data: usersData, isLoading } = trpc.user.list.useQuery(
    authUser?.companyId ? { companyId: authUser.companyId } : undefined,
    {
      enabled: !!authUser?.companyId && canManageUsers,
      staleTime: 60_000,
    },
  );

  const users = useMemo<UserSummary[]>(() => {
    if (!usersData) {
      return [];
    }
    const response = usersData as UserListResponse;
    if (Array.isArray(response.data)) {
      return response.data;
    }
    if (Array.isArray(response.items)) {
      return response.items;
    }
    if (Array.isArray(response.users)) {
      return response.users;
    }
    return [];
  }, [usersData]);

  const options = useMemo(() => {
    return users.map((u) => ({
      label: u.name ?? u.email ?? u.id,
      value: u.id,
    }));
  }, [users]);

  const handleAssignMe = (userId: string) => {
    if (onChange) {
      onChange(userId);
    }
  };

  const handleChange = (selectedValue: string | undefined) => {
    if (onChange) {
      onChange(selectedValue);
    }
  };

  const isDisabled = disabled || !canManageUsers || users.length === 0;

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Select
        value={value}
        onChange={handleChange}
        allowClear={allowClear}
        placeholder={placeholder}
        style={{ flex: 1, ...style }}
        disabled={isDisabled}
        loading={isLoading}
        showSearch
        filterOption={(input, option) =>
          (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
        }
        options={options}
      />
      {showAssignMeButton && (
        <SelfAssignButton
          onAssign={handleAssignMe}
          currentValues={value ? [value] : []}
          tooltip={assignMeTooltip}
          size="middle"
          disabled={isDisabled}
        />
      )}
    </Space.Compact>
  );
};

export default SingleUserSelect;
